import { Chart, ChartDataSets, ChartOptions } from 'chart.js';

/**
 * Estilo de las barras del Dashboard, copiado de Estadisticas de Skuld OSINT
 * (modules/dashboard/utils/chart-theme.ts) y traducido a Chart.js 2.9:
 * scales.xAxes/yAxes, legend y tooltips en la raiz, gridLines y fontColor.
 * No toca Chart.defaults.global: solo afecta a quien usa estas funciones.
 */

/** Paleta de series de OSINT, en orden fijo. */
export const SERIES = [
  '#2a78d6', // azul
  '#eb6834', // naranja
  '#1baf7a', // aqua
  '#eda100', // amarillo
  '#e87ba4', // magenta
  '#008300', // verde
  '#4a3aa7', // violeta
  '#e34948', // rojo
];

/** Tinta y rejilla de OSINT. */
export const INK = {
  primario: '#0b0b0b',
  secundario: '#52514e',
  apagado: '#898781',
  rejilla: '#e1e0d9',
  base: '#c3c2b7',
  superficie: '#ffffff',
};

/** Fuente de OSINT, solo en los ejes y el tooltip de estos graficos. */
const FUENTE = 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';

/**
 * Dibujo de barra de fabrica de Chart.js, guardado una sola vez al cargar este modulo.
 * by-analist, finalized-by-analyst y without-assigned-unit-bar reemplazan en su ngOnInit
 * el draw global por uno de barras redondeadas, y delegan en este para las barras horizontales.
 */
export const dibujarBarraOriginal = (Chart as any).elements.Rectangle.prototype.draw;

/** Etiquetas y valores juntos, de mayor a menor (empate: por nombre, para que no cambie entre cargas). */
export function deMayorAMenor(etiquetas: string[], valores: number[]) {
  const orden = valores
    .map((_, i) => i)
    .sort((a, b) => valores[b] - valores[a] || String(etiquetas[a]).localeCompare(String(etiquetas[b])));
  return { etiquetas: orden.map((i) => etiquetas[i]), valores: orden.map((i) => valores[i]) };
}

/**
 * Opciones de una barra horizontal de una sola serie.
 * Responsiva: el alto lo pone el contenedor (position: relative + height).
 */
export function opcionesBarraHorizontal(): ChartOptions {
  return {
    responsive: true,
    maintainAspectRatio: false,
    // Una sola serie: el titulo del card ya dice que se esta mirando
    legend: { display: false },
    scales: {
      xAxes: [
        {
          // Los recuentos son enteros: sin precision el eje muestra 0,5
          ticks: {
            beginAtZero: true,
            min: 0,
            precision: 0,
            fontColor: INK.apagado,
            fontFamily: FUENTE,
            // En v2 el padding es 0: sin marcas (drawTicks) los numeros quedan pegados a la rejilla
            padding: 10,
          },
          gridLines: {
            color: INK.rejilla,
            zeroLineColor: INK.rejilla,
            drawBorder: false,
            drawTicks: false,
          },
        },
      ],
      yAxes: [
        {
          gridLines: { display: false },
          ticks: {
            fontColor: INK.secundario,
            fontFamily: FUENTE,
            // Chart.js 2 le da al eje vertical como mucho la mitad del ancho: se recorta
            // a ~7 px por letra segun el ancho real (this es la escala). El tooltip muestra el nombre completo.
            callback: function (valor) {
              const max = Math.max(4, Math.floor((this.chart.width * 0.45) / 7));
              const texto = String(valor);
              return texto.length > max ? texto.slice(0, max - 1).trim() + '…' : texto;
            },
          },
        },
      ],
    },
    tooltips: {
      backgroundColor: INK.primario,
      cornerRadius: 6,
      xPadding: 10,
      yPadding: 10,
      displayColors: false,
      titleFontFamily: FUENTE,
      bodyFontFamily: FUENTE,
    },
  };
}

/** Estilo del dataset de barras: color de la serie y aire entre barras. */
export function serieBarra(color: string): ChartDataSets {
  return {
    backgroundColor: color,
    barPercentage: 0.72,
    categoryPercentage: 0.78,
    maxBarThickness: 44,
  };
}
